import { setText, toggleClass, isNumeric } from '../helpers/attribute-helper';
import { getLeadTime, getEstimatedMonth, days, weeks } from '../../utils/lead-time-helper';

export default class LeadTime {
  constructor(parent) {
    this.parent = parent;
    this.el = parent.el;
    this.pricing = parent.pricing;
    this.car = parent.car;
    this.navigation = parent.navigation;
    this.dateLocale = this.el.dataset.dateLocale;
    this.leadTimeDropdown = this.el?.querySelector('.lead-time-dropdown');
    this.leadTimeModal = this.el?.querySelector('#lead-time-modal');
    this.leadTimeSection = this.el?.querySelector('.lead-time-section');
    this.lastFocusedElement = null;
    this.leadTimeEditLinks = this.el?.querySelectorAll('.lead-time-table-icon-col a');
    this.bind();
  }

  bind() {
    if(this.leadTimeDropdown) {
      this.leadTimeDropdown.addEventListener('click', (event) => {
        this.toggleModal();
      });

      // close if user tabs out completely
      this.leadTimeSection.addEventListener("focusout", (e) => {
        setTimeout(() => {
          const active = document.activeElement;
          const isStillInside = this.leadTimeSection.contains(active);

          if (!isStillInside) {
            this.hide();
          }
        }, 0);
      });

      // close if user tabs 'Escape'
      this.leadTimeModal.addEventListener('keydown', (e) => {
            if (e.code === 'Escape') {
                this.hide();
                if(this.lastFocusedElement) {
                  this.lastFocusedElement.focus({ preventScroll: true });
                }
            }
        });

      this.el.addEventListener('click', (e) => {
        const isClickInsideModal = this.leadTimeModal.contains(e.target);
        const isClickOnButton = e.target.closest('.lead-time-dropdown');
        
        if (!isClickInsideModal && !isClickOnButton) {
            if (this.leadTimeDropdown.getAttribute("aria-expanded") === "true") {
              this.hide();
            }
        }
      });

      this.leadTimeEditLinks.forEach((el) => el.addEventListener('click', (e) => {
        e.preventDefault();
        const target = e.target.closest('a').getAttribute('href');
        this.hide();
        this.navigation.scrollToSection(target);
      }));
    }
  }

  toggleModal() {
    const isOpen = this.leadTimeDropdown.getAttribute("aria-expanded") === "true";
    isOpen ? this.hide() : this.openModal();
  }

  openModal() {
     this.lastFocusedElement = document.activeElement;
    this.leadTimeDropdown.classList.add('open');
    this.leadTimeDropdown.setAttribute("aria-expanded", "true");
    this.leadTimeModal.setAttribute("tabindex", "-1");
    this.leadTimeModal.focus({ preventScroll: true });
  }

  hide() {
    this.leadTimeDropdown.classList.remove('open');
    this.leadTimeDropdown.setAttribute("aria-expanded", "false");
  }

  async update() {
    await this.pricing.waitForMTOPricing();

    const gradeDates = this.getGradeDates();
    setText(this.el, gradeDates, 'data-grade-lead-time', (val, el) => getEstimatedMonth(val, this.dateLocale));
    toggleClass(this.el, gradeDates, 'data-grade-lead-time-toggle', 'hide', false, (cond) => !!cond);

    const carGradeDate = this.getCarGradeDate(gradeDates);
    const carGradeMonth =  getEstimatedMonth(carGradeDate, this.dateLocale);
    setText(this.el, carGradeMonth, 'data-car-grade-lead-time');
    toggleClass(this.el, carGradeMonth, 'data-car-grade-lead-time-toggle', 'hide', false, (cond) => !!cond);

    const carColourDate = this.getCarColourDate();
    const carColourDays = days(carGradeDate, carColourDate);
    const carColourWeeks = weeks(carColourDays);
    setText(this.el, carColourWeeks, 'data-car-colour-lead-time');
    toggleClass(this.el, carColourWeeks, 'data-car-colour-lead-time-toggle', 'hide', false, (val) => isNumeric(val) && val > 0);

    const carDate = this.getCarDate();
    const carTrimDays = days(carColourDate, carDate);
    const carTrimWeeks = weeks(carTrimDays);
    setText(this.el, carTrimWeeks, 'data-car-trim-lead-time');
    toggleClass(this.el, carTrimWeeks, 'data-car-trim-lead-time-toggle', 'hide', false, (val) => isNumeric(val) && val > 0);

    const carColourTrimDays = carColourDays + carTrimDays;
    const carColourTrimWeeks = weeks(carColourTrimDays);
    setText(this.el, carColourTrimWeeks, 'data-car-colour-trim-lead-time');
    toggleClass(this.el, carColourTrimWeeks, 'data-car-colour-trim-lead-time-toggle', 'hide', false, (val) => isNumeric(val) && val > 0);

    const carMonth = getEstimatedMonth(carDate, this.dateLocale);
    setText(this.el, carMonth, 'data-car-lead-time');
    toggleClass(this.el, carMonth, 'data-car-lead-time-toggle', 'hide', false, (cond) => !!cond);
  }

  getGradeDates() {
    return this.pricing.mtoPricing?.data?.reduce((map, mtoPricing) => {
      const gradeCode = mtoPricing['c_gradeCode'];
      map[gradeCode] = getLeadTime(mtoPricing);
      return map;
    }, {}) || {};
  }

  getCarGradeDate(gradeDates) {
    const mtoPricing = this.pricing.getCarMTOPricing();
    const grade = mtoPricing['c_gradeCode'];
    return gradeDates[grade];
  }

  getCarColourDate() {
    const mtoPricing = this.pricing.getCarMTOPricing();
    const colour = this.car.colourTrim().colourMTOCE;
    return getLeadTime(mtoPricing, (mtocc) => mtocc['c_extColourCode'] === colour);
  }

  getCarDate(tempCar) {
    const car = tempCar ?? this.car;
    const mtoPricing = this.pricing.getCarMTOPricing(car.mto());
    const colourTrim = car.colourTrim();
    const colour = colourTrim.colourMTOCE;
    const trim = colourTrim.trimMTOCE;
    return getLeadTime(mtoPricing, (mtocc) => mtocc['c_extColourCode'] === colour && mtocc['c_intColourCode'] === trim);
  }

  getCarEstimatedMonth(car) {
    const date = this.getCarDate(car);
    return getEstimatedMonth(date, this.dateLocale);
  }
}